import React, { useRef, useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Link } from 'react-router';
import i18n from 'i18next';
import { useDispatch } from 'react-redux';
import { coreUtils, Namespace } from '@gymcloud/core';

import config from 'web-config';
import { SetNamespace, NamespaceLogo } from 'web-components';
import { Flex, Text, icons, isMobile } from 'web-styled';
import { trackAnalyticsEvent } from 'web-utils';

import gcScreenshot from 'web-assets/images/purchase/default-screenshot-gc.png';
import StepOneForm from './StepOneForm';
import StepTwoForm from './StepTwoForm';
import * as S from './styled';

const stripePromise = loadStripe(config.stripe.key);
const STEPS = {
  ACCOUNT_CREATION: 'one',
  PAYMENT: 'two'
} as const;

const formatPrice = (amount: number): string => {
  const isFloat = Number(amount) === amount && amount % 1 !== 0;
  return isFloat ? amount.toFixed(2) : amount.toString();
};

const getFormattedAmount = (amount: number): string[] =>
  formatPrice(amount).split('.');

// Types
interface Item {
  id: string;
  name: string;
  amount: number;
  interval?: string;
  description?: string;
}

interface UserData {
  [key: string]: any;
}

interface Props {
  item: Item;
  namespace: Namespace;
  isPlan?: boolean;
  isTest?: boolean;
  formTitle?: React.ReactNode;
  buttonText?: string;
  secondButtonText?: string;
  renderConfirmation?: (skipped: boolean) => React.ReactNode;
  onPlanChange?: (plan: any) => void;
}

type Step = typeof STEPS[keyof typeof STEPS];

const PriceDisplay: React.FC<{ item: Item }> = ({ item }) => (
  <Text
    size={isMobile ? '20px' : '32px'}
    color="#ffffff"
    weight="bold"
  >
    ${getFormattedAmount(item.amount).join('.')}
    {item.interval && (
      <span>/{coreUtils.getIntervalABBR(item.interval)}</span>
    )}
  </Text>
);

const ItemDescription: React.FC<{ description: string }> = ({ description }) => (
  <Flex margin="10px 0 0 0">
    <Text
      color="#ffffff"
      size={isMobile ? '14px' : '18px'}
      whiteSpace="break-spaces"
    >
      {description}
    </Text>
  </Flex>
);

const AppDownloadLinks: React.FC<{ namespace: Namespace }> = ({ namespace }) => {
  if (namespace.symbol === 'strongfirst') {
    return (
      <>
        <Text color="#121827" align="center">
          Download the StrongFirst app now, then log in with your email and password.
        </Text>
        <S.Links>
          <a href="https://apps.apple.com/us/app/strongfirst/id6451005125">
            <img width="90.5%" src={icons.appStore} alt="Download on App Store" />
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.strongfirst.mobile">
            <img width="100%" src={icons.googlePlay} alt="Get it on Google Play" />
          </a>
        </S.Links>
      </>
    );
  }

  return (
    <>
      <Flex width="250px" spacing={5} column>
        <Text color="#121827">
          1. Go to the{' '}
          <a href={config.mobile.app_store_url}>App Store</a>
          {' or '}
          <a href={config.mobile.google_play_url}>Google Play</a>
        </Text>
        <Text color="#121827">
          2. Search "gymcloud" and download "Training App (by GymCloud)"
        </Text>
        <Text color="#121827">
          3. Log in with your email + password
        </Text>
        <Text color="#121827">
          4. Your app icon will be updated and you can now access the{' '}
          {namespace.name} Training App
        </Text>
      </Flex>
      <S.Links>
        <a href={config.mobile.app_store_url}>
          <img width="90.5%" src={icons.appStore} alt="Download on App Store" />
        </a>
        <a href={config.mobile.google_play_url}>
          <img width="100%" src={icons.googlePlay} alt="Get it on Google Play" />
        </a>
      </S.Links>
    </>
  );
};

const ConfirmationScreen: React.FC<{
  namespace: Namespace;
  isSkipped: boolean;
  renderConfirmation?: (skipped: boolean) => React.ReactNode;
}> = ({ namespace, isSkipped, renderConfirmation }) => (
  <S.FormWrap>
    <Flex column align="center" width="100%" padding="20px" spacing={20}>
      <Text
        size="1.5em"
        color="#121827"
        weight="bold"
        align="center"
      >
        {renderConfirmation ? renderConfirmation(isSkipped) : 'Thank You for Your Purchase!'}
      </Text>
      <Flex width="250px" spacing={5} column>
        <AppDownloadLinks namespace={namespace} />
      </Flex>
    </Flex>
  </S.FormWrap>
);

const ProductDisplay: React.FC<{ item: Item; namespace: Namespace }> = ({ item, namespace }) => (
  <S.FormData>
    {isMobile && <NamespaceLogo />}

    <Flex column align="center" spacing={30}>
      <S.AppScreen
        src={namespace.screen_app_url?.url || gcScreenshot}
        alt={`${item.name} preview`}
      />
      <Flex column align="center" spacing={10}>
        <Text
          size={isMobile ? '20px' : '40px'}
          color="#ffffff"
          align="center"
          weight="bold"
        >
          {item.name}
        </Text>
        <PriceDisplay item={item} />
        {item.description && <ItemDescription description={item.description} />}
      </Flex>
    </Flex>
  </S.FormData>
);

const FormStep: React.FC<{
  step: Step;
  formTitle?: React.ReactNode;
  isPlan: boolean;
  isTest: boolean;
  buttonText: string;
  secondButtonText: string;
  userData: UserData;
  namespace: Namespace;
  item: Item;
  onStepOneSubmit: (user: UserData) => void;
  onStepTwoSuccess: (skipped: boolean) => void;
  onPlanChange?: (plan: any) => void;
}> = ({
  step,
  formTitle,
  isPlan,
  isTest,
  buttonText,
  secondButtonText,
  userData,
  namespace,
  item,
  onStepOneSubmit,
  onStepTwoSuccess,
  onPlanChange
}) => {
    const getTitle = () => isTest ? 'Set Up Your Account' : 'Create Your Account';

    const renderTrialText = () => isPlan && !isTest && (
      <Text align="center">
        Enjoy premium content FREE for 7 Days
      </Text>
    );

    if (step === STEPS.ACCOUNT_CREATION) {
      return (
        <>
          <S.FormTitle>
            {formTitle || getTitle()}
            {renderTrialText()}
          </S.FormTitle>
          <StepOneForm
            isTest={isTest}
            userData={userData}
            namespace={namespace}
            buttonText={buttonText}
            onSubmit={onStepOneSubmit}
          />
          <Flex spacing={3} width="100%" justify="center" align="center" padding="10px">
            <Text color="#888" size="14px">
              Already have an account?
            </Text>
            <Link to="/login">Sign in</Link>
          </Flex>
        </>
      );
    }

    return (
      <>
        <S.FormTitle>
          <span>Enter Your Payment Information</span>
          {isPlan && (
            <Text align="center">
              Enjoy premium content FREE for 7 Days
            </Text>
          )}
        </S.FormTitle>
        <Elements stripe={stripePromise}>
          <StepTwoForm
            id={item.id}
            isPlan={isPlan}
            secondButtonText={secondButtonText}
            userData={userData}
            onSuccess={onStepTwoSuccess}
            onPlanChange={onPlanChange}
          />
        </Elements>
      </>
    );
  };

const SignUpWithPurchaseForm: React.FC<Props> = ({
  item,
  formTitle,
  namespace,
  isPlan = false,
  isTest = false,
  buttonText = 'Create My Account',
  secondButtonText = 'Complete Purchase',
  renderConfirmation,
  onPlanChange,
}) => {
  const form = useRef<HTMLFormElement>(null);
  const [step, setStep] = useState<Step>(STEPS.ACCOUNT_CREATION);
  const [isSkipped, setSkipped] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [userData, setUserData] = useState<UserData>({});
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({
      type: 'GC_ANALYTICS',
      eventKey: 'signup_viewed',
    });
  }, [dispatch]);

  const handleStepOneSubmit = useCallback((user: UserData) => {
    if (isTest) {
      setLoaded(true);
    } else {
      setUserData(user);
      setStep(STEPS.PAYMENT);
    }

    trackAnalyticsEvent('signup_clicked_create_account');
    form.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isTest]);

  const handleStepTwoSuccess = useCallback((skipped: boolean) => {
    setSkipped(skipped);
    setLoaded(true);
  }, []);

  if (isLoaded) {
    return (
      <Flex>
        <ConfirmationScreen
          namespace={namespace}
          isSkipped={isSkipped}
          renderConfirmation={renderConfirmation}
        />
      </Flex>
    );
  }

  return (
    <S.Wrapper>
      <ProductDisplay item={item} namespace={namespace} />

      <Flex column spacing={30} padding="35px">
        {!isMobile && <NamespaceLogo />}

        <S.FormWrap ref={form}>
          <S.FormInner>
            <FormStep
              step={step}
              formTitle={formTitle}
              isPlan={isPlan}
              isTest={isTest}
              buttonText={buttonText}
              secondButtonText={secondButtonText}
              userData={userData}
              namespace={namespace}
              item={item}
              onStepOneSubmit={handleStepOneSubmit}
              onStepTwoSuccess={handleStepTwoSuccess}
              onPlanChange={onPlanChange}
            />

            {process.env.TARGET_ENV !== 'production' && (
              <Flex width="100%" margin="-10px 0px 10px 0px" justify="center">
                <SetNamespace />
              </Flex>
            )}
          </S.FormInner>

          {isPlan && (
            <S.Statement>
              {i18n.t('subscription.renewal_text')}
            </S.Statement>
          )}
        </S.FormWrap>
      </Flex>
    </S.Wrapper>
  );
};

export default SignUpWithPurchaseForm;
